import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { MAINLINE_TASKS } from "../content/world";
import { completeLearningTask } from "../services/learning/completion";
import { recordExploreEvent } from "../services/explore/exploreActivity";
import { useAppStore } from "../store/useAppStore";
import { MissionThreePage } from "./MissionThreePage";

type MainlineMessage = {
  source?: string;
  type?: "ready" | "home" | "complete";
  taskId?: string;
  payload?: Record<string, unknown>;
};

const legacyFolders: Record<string, string> = { day1: "d01", day2: "d02", day3: "d03" };

export function MainlineTaskPage() {
  const { taskId } = useParams();
  if (taskId === "mission3") return <MissionThreePage />;
  return <ClassicMainlineTaskPage taskId={taskId} />;
}

function ClassicMainlineTaskPage({ taskId }: { taskId?: string }) {
  const navigate = useNavigate();
  const task = useMemo(() => MAINLINE_TASKS.find((entry) => entry.id === taskId), [taskId]);
  const { activeChildId, completeTask, setStars } = useAppStore();
  const [ready, setReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const completionRef = useRef(false);

  const complete = useCallback(async (payload: Record<string, unknown> = {}) => {
    if (!task || completionRef.current) return;
    completionRef.current = true;
    void recordExploreEvent(activeChildId, "mainline.completed", {
      itemId: task.id,
      kind: "mainline",
      source: "classic-runner",
      ...payload
    });
    const result = await completeLearningTask(activeChildId, task.id, task.title, 3);
    completeTask(task.id, result.awardedStars);
    if (result.totalStars !== null) setStars(result.totalStars);
  }, [activeChildId, completeTask, setStars, task]);

  useEffect(() => {
    completionRef.current = false;
    setReady(false);
    setLoadFailed(false);
    if (!task) return;
    void recordExploreEvent(activeChildId, "mainline.opened", {
      itemId: task.id,
      kind: "mainline",
      source: "classic-runner"
    });

    const receive = (event: MessageEvent<MainlineMessage>) => {
      if (event.origin !== window.location.origin || event.source !== frameRef.current?.contentWindow || event.data?.source !== "lusky-mainline") return;
      if (event.data.taskId && event.data.taskId !== task.id) return;
      if (event.data.type === "ready") { setReady(true); setLoadFailed(false); }
      if (event.data.type === "home") navigate("/");
      if (event.data.type === "complete") void complete(event.data.payload);
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [activeChildId, complete, navigate, task, attempt]);

  useEffect(() => {
    if (ready) return;
    const timer = window.setTimeout(() => setLoadFailed(true), 20_000);
    return () => window.clearTimeout(timer);
  }, [ready, taskId, attempt]);

  if (!task || !taskId || !legacyFolders[taskId]) return <Navigate to="/" replace />;
  const src = `/mainline/${legacyFolders[taskId]}/index.html?task=${encodeURIComponent(taskId)}`;

  return <section className="classic-mainline-host" aria-label={`${task.title}沉浸式任务`}>
    {!ready && !loadFailed && <div className="classic-mainline-loading"><i>✦</i><b>正在打开星光任务……</b><span>图片与语音准备中</span></div>}
    {loadFailed && <div className="classic-mainline-error" role="alert"><b>任务舞台暂时没有打开</b><p>请检查网络；如有新版本提示，请更新后重试。</p><button type="button" onClick={() => { setReady(false); setLoadFailed(false); setAttempt(value => value + 1); }}>重新打开任务</button><button type="button" onClick={() => navigate("/")}>返回星光小屋</button></div>}
    <iframe
      key={`${taskId}:${activeChildId}:${attempt}`}
      ref={frameRef}
      className={ready ? "is-ready" : ""}
      src={src}
      title={task.title}
      allow="autoplay; fullscreen"
      onError={() => setLoadFailed(true)}
    />
  </section>;
}
